import { useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useNavigationType } from "react-router-dom";
import CustomBreadcrumb from "../../../components/customBreadcrumb/CustomBreadcrumb";
import MainLoader from "../../../components/loading/MainLoader";
import SearchBar from "../../../components/searchBar/SearchBar";
import StatusAlertModel from "../../../components/statusAlertModel/StatusAlertModel";
import Title from "../../../components/title/Title";
import {
  ERROR_FAULT_HISTORY,
  LOADING_FAULT_HISTORY,
  SUCCESS_FAULT_HISTORY,
} from "../../../const/actionTypes";
import { IServiceFaultListItem } from "../../../interfaces/fault";
import { ISuccessState } from "../../../interfaces/successState";
import { getFaultList } from "../../../services/fault";
import { clearFaultHistoryList } from "../../../store/actions/fault.action";
import { IRootState } from "../../../store/configureStore";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import { useMediaQuery } from "@mui/material";

const FaultList = () => {
  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const navigationType = useNavigationType();
  const [searchValueResponse, setSearchResponseValue] = useState<string>("");
  const [isVerrified, setIsVerified] = useState(false);

  const { loading, faultList } = useSelector(({ fault }: IRootState) => ({
    loading: fault.loadingFaultHistory,
    faultList: fault.faultHistory,
  }));
  const [isSuccess, setIsSuccess] = useState<ISuccessState | undefined>();

  useEffect(() => {
    if (navigationType === "PUSH") {
      dispatch(clearFaultHistoryList());
    }
  }, [navigationType, dispatch]);

  const searchHandler = (value: string) => {
    if (!value) {
      return;
    }

    dispatch({
      type: LOADING_FAULT_HISTORY,
    });
    getFaultList(value)
      .then((data) => {
        dispatch({
          type: SUCCESS_FAULT_HISTORY,
          payload: data,
        });
        setIsVerified(true);
        setSearchResponseValue(value);
        if (data.length === 0) {
          setIsSuccess({
            isSuccess: false,
            message: "No Records Found",
          });
        }
      })
      .catch((err) => {
        setIsSuccess({
          isSuccess: false,
          message: err.message,
        });
        setIsVerified(false);
        dispatch({
          type: ERROR_FAULT_HISTORY,
        });
      });
  };

  // Define columns for DataGrid
  const columns: GridColDef<IServiceFaultListItem>[] = [
    {
      field: "srNumber",
      headerName: "Fault Reference",
      headerClassName: "super-app-theme--header",
      width: 200,
    },
    {
      field: "openedDate",
      headerName: "Opened Date",
      headerClassName: "super-app-theme--header",
      width: 220,
    },

    // {
    //   field: "ticketType",
    //   headerName: "Type of Complaint",
    //   headerClassName: "super-app-theme--header",
    //   width: 150,
    // },
    {
      field: "clearedOn",
      headerName: "Cleared Date & Time",
      headerClassName: "super-app-theme--header",
      width: 200,
    },
    {
      field: "outageDurationInDays",
      headerName: "Outage Duration\n(Days)",
      headerClassName: "super-app-theme--header",
      align: "center",
      width: 200,
    },
    {
      field: "subStatus",
      headerName: "Complaint Status",
      headerClassName: "super-app-theme--header",
      width: 200,
      align: "center",
      renderCell: (params) => {
        if (params.row.subStatus === "Resolved") {
          return params.row.subStatus;
        }
        return (
          <Button
            size="sm"
            onClick={() =>
              navigate(`/tracker/status/faultProgress`, {
                state: { srNumber: params.row.srNumber },
              })
            }
          >
            {params.row.subStatus}
          </Button>
        );
      },
    },
    // {
    //   field: "actions",
    //   headerName: "",
    //   width: 150,
    //   renderCell: (params) => {
    //     if (params.row.subStatus === "Resolved") {
    //       return null;
    //     }
    //     return (
    //       <Button
    //         size="sm"
    //         onClick={() =>
    //           navigate(`/tracker/status/faultProgress`, {
    //             state: { srNumber: params.row.srNumber },
    //           })
    //         }
    //       >
    //         View Status
    //       </Button>
    //     );
    //   },
    // },
  ];

  const rows = faultList.map((fault, index) => ({
    id: index + 1,
    srNumber: fault.srNumber,
    openedDate: fault.openedDate,
    ticketType: fault.ticketType,
    clearedOn: fault.clearedOn,
    outageDurationInDays: fault.outageDurationInDays,
    subStatus: fault.subStatus,
    commMethod: fault.commMethod,
    area: fault.area,
    ossReferenceNumber: fault.ossReferenceNumber,
    status: fault.status,
    srType: fault.srType,
    insArea: fault.insArea,
    sltEnterpriseCustomerSegment: fault.sltEnterpriseCustomerSegment,
    description: fault.description,
    sltAltContactNumber: fault.sltAltContactNumber,
    serviceId: fault.serviceId,
    ttSource: fault.ttSource,
    subArea: fault.subArea,
    customerReferenceNumber: fault.customerReferenceNumber,
  }));

  return (
    <div className="page-container">
      <MainLoader show={loading} />
      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          { path: "/status/faultHistory", title: "Complaint Records" },
        ]}
      />
      <Title>Complaint Records</Title>

      <Row className="justify-content-center mt-4">
        <Col className="col-md-5">
          <SearchBar
            placeholder="Service ID"
            onSearch={searchHandler}
            defaultValue={
              faultList.length > 0 ? faultList[0].serviceId : undefined
            }
          />
        </Col>
      </Row>

      <p style={{ textAlign: "center", marginTop: 15, color: "gray" }}>
        Recent 10 service complaint records are displayed.
      </p>

      <div
        style={{
          opacity: isVerrified ? 1 : 0.6,
          pointerEvents: isVerrified ? "auto" : "none",
          filter: isVerrified ? "none" : "blur(2px)",
          backgroundColor: isVerrified ? "white" : "#f0f0f0",
          transition: "all 0.3s ease-in-out",
          borderRadius: "8px",
        }}
      >
        <Row className="page-sub-containerV3 justify-content-center mt-3 flex-grow-1">
          <Col xs={12} className="d-flex flex-column" style={{ minHeight: 0 }}>
            <Paper
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                height: "100%",
                minHeight: 300,
                overflow: "auto",
                p: 1,
                mx: "auto",
                width: "100%",
                "& .MuiTablePagination-root": {
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "flex-end",
                  minHeight: "56px",
                  gap: "8px",
                },
                "& .MuiTablePagination-spacer": {
                  order: 2,
                },
                "& .MuiTablePagination-selectLabel": {
                  order: 0,
                  display: "flex",
                  alignItems: "baseline",
                  padding: "15px",
                  height: "36px",
                },
                "& .MuiTablePagination-select": {
                  order: 1,
                  display: "flex",
                  alignItems: "center",
                  height: "36px",
                },
                "& .MuiTablePagination-displayedRows": {
                  order: 3,
                  display: "flex",
                  padding: "15px",
                  alignItems: "baseline",
                  height: "36px",
                },
                "& .MuiTablePagination-actions": {
                  order: 4,
                  display: "flex",
                  alignItems: "center",
                  marginLeft: "auto",
                },
              }}
            >
              <DataGrid<IServiceFaultListItem>
                rows={rows}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: {
                      page: 0,
                      pageSize: 5,
                    },
                  },
                }}
                pageSizeOptions={[5, 10]}
                sx={{
                  border: 0,
                  flex: 1,
                  "& .MuiDataGrid-virtualScroller": {
                    overflow: "auto",
                  },
                  "& .super-app-theme--header": {
                    backgroundColor: "rgba(0, 153, 255, 0.55)",
                  },
                  "& .MuiDataGrid-cell": {
                    fontSize: (theme) => ({
                      xs: "0.75rem",
                      sm: "0.875rem",
                    }),
                  },
                }}
                disableVirtualization={useMediaQuery("(max-width:600px)")}
              />
            </Paper>
          </Col>
        </Row>
      </div>
      <StatusAlertModel isSuccess={isSuccess} setIsSuccess={setIsSuccess} />
    </div>
  );
};

export default FaultList;
